import React from 'react';
import { cn } from '../utils/cn';
import { today } from '../utils/format';

export interface ILMSDatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  defaultToday?: boolean;
  containerClassName?: string;
}

/** Thin wrapper over the native date input with ILMS label/error conventions and a "default to today" convenience. */
export const ILMSDatePicker = React.forwardRef<HTMLInputElement, ILMSDatePickerProps>(function ILMSDatePicker(
  { label, error, helperText, required, defaultToday, id, className, containerClassName, value, defaultValue, ...rest },
  ref
) {
  const inputId = id ?? `date-${label?.replace(/\s+/g, '-').toLowerCase() ?? Math.random().toString(36).slice(2)}`;
  const resolvedDefault = defaultValue ?? (defaultToday && value === undefined ? today() : undefined);
  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-label text-ink-700">
          {label} {required && <span className="text-critical-600">*</span>}
          {!required && <span className="text-helper-text text-ink-400 font-normal"> (optional)</span>}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        type="date"
        value={value}
        defaultValue={resolvedDefault}
        className={cn(
          'h-11 min-h-touch rounded-md border px-3 text-body bg-white',
          'focus-visible:outline-2 focus-visible:outline-brand-green-500',
          error ? 'border-critical-500' : 'border-ink-300/50',
          className
        )}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        {...rest}
      />
      {error && (
        <p id={`${inputId}-error`} className="text-error-text text-critical-600">
          {error}
        </p>
      )}
      {!error && helperText && <p className="text-helper-text text-ink-400">{helperText}</p>}
    </div>
  );
});
